// main imports
import React, { Component } from "react";
import { connect } from "react-redux";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { debounce } from "es-toolkit";

// backend
import { getBackend } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as BusinessLogic from "@/lib/businessLogic";

// import subcomponents
import MaterialsList from "./MaterialsList";
import MaterialsDetails from "./MaterialsDetails";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

import * as Redirect from "@/app/URLHandler/redirect";
import { ResourcesLayout } from "@/modules/shared/MainContent";
import * as Auth from "@/lib/auth";
import Authorization from "@/modules/shared/Authorization";
import { classNames } from "@/lib/util";

import "./materialsNew.scss";

import type { ReduxState } from "@/store/reducers";

type OwnProps = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

type Props = OwnProps & ReduxProps;

type State = {
    materials: t.IMaterialModel[];
    parallel_materials: t.IParallelMaterialModel[];
    selected: t.IMaterialModel | null;
    selected_type_external: string | null;
    selected_plant_tags: t.IMaterialPlantModel[] | null;
    parallel_selected: t.IParallelMaterialModel | null;
    error: string;
    warning: string;
    only_bottleneck: boolean;
    offset: number;
    last_row: number;
    type_externals: string[];
    has_next_page: boolean;
    is_pagination: boolean;
    /** Local value for search input (synced from props, debounced to Redux). */
    title_filter_input: string;
};

const PARALLEL_MATERIALS_ID = "parallel";
const SEARCH_DEBOUNCE_MS = 150;

const MaterialTabs = (props: {
    is_parallel: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
    const { is_parallel, onChange } = props;
    return (
        <div className="nav nav-tabs material-tabs" role="tablist">
            <label className={classNames("nav-link", { active: !is_parallel })}>
                <input type="radio" name="options" value="" checked={!is_parallel} onChange={onChange} />
                {translate("common.materials", "Materials")}
            </label>
            <label className={classNames("nav-link", { active: is_parallel })}>
                <input
                    type="radio"
                    name="options"
                    value={PARALLEL_MATERIALS_ID}
                    checked={is_parallel}
                    onChange={onChange}
                />
                {translate("Manufacturing.Materials.parallel_materials", "Multipart materials")}
            </label>
        </div>
    );
};

/**
 * Displaying materials list.
 */
class Materials extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            materials: [],
            parallel_materials: [],
            selected: null,
            selected_type_external: null,
            parallel_selected: null,
            error: "",
            warning: "",
            only_bottleneck: false,
            offset: 0,
            last_row: 0,
            type_externals: [],
            has_next_page: false,
            selected_plant_tags: null,
            is_pagination: false,
            title_filter_input: props.resource_edit_filters.title_filter ?? "",
        };
    }

    debouncedApplyTitleFilter = debounce((value: string, uuid: string) => {
        Redirect.resourceEditFilters(this.props.history, "/digital-twin/resources/materials/" + uuid, {
            ...this.props.resource_edit_filters,
            title_filter: value,
        });
    }, SEARCH_DEBOUNCE_MS);

    getMaterialUuid = (): string => {
        const uuid = this.props.match.params.uuid;
        return typeof uuid === "string" ? uuid : "";
    };

    fetchNext = () => {
        this.setState({ offset: this.state.offset + 1 });
    };

    async loadComponent() {
        try {
            // prepare map of line-group names for each line
            await BusinessLogic.waitForManufacturingAsync();
            this.loadMaterials();
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    }

    /**
     * Loading materials for selected production line.
     *
     * @param {string} line_group_uuid Line uuid.
     * @param {boolean} only_bottleneck Bottleneck indicator.
     */
    async loadMaterials() {
        const parallel_materials: t.IParallelMaterialModel[] = [];
        try {
            const is_parallel = this.props.resource_edit_filters.line_group_uuid === PARALLEL_MATERIALS_ID;
            const limit = 500;
            const res = await getBackend().manufacturing.searchMaterialsAll({
                limit,
                offset: this.state.offset * limit,
                search_query: this.props.resource_edit_filters.title_filter
                    ? this.props.resource_edit_filters.title_filter
                    : undefined,
                type_external: is_parallel ? undefined : (this.state.selected_type_external ?? undefined),
                parallel: is_parallel,
            });
            const materials = res.materials;
            const count = res.count;
            const type_externals = res.type_externals;

            // remember the materials
            this.setState(
                {
                    materials: this.state.is_pagination ? [...this.state.materials, ...materials] : [...materials],
                    type_externals,
                    parallel_materials,
                    error: "",
                    warning: "",
                    has_next_page: this.state.offset * limit < count - limit,
                    is_pagination: false,
                },
                () => {
                    // select the first on the list if we have any
                    let material: t.IMaterialModel | null = null;
                    const uuid = this.getMaterialUuid();
                    if (this.state.materials.length > 0) {
                        material = this.state.materials.find(m => m.uuid === uuid) || this.state.materials[0];
                    }

                    if (material !== null) {
                        this.handleMaterialClick(material);
                    } else {
                        this.setState({
                            selected: null,
                            parallel_selected: null,
                        });
                    }
                }
            );
        } catch (err) {
            console.log("errr", err);
            this.setState({ error: (err as Error).message });
        }
    }

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.currentTarget;
        const value = target.value;
        const name = target.name;

        const uuid = this.state.selected ? this.state.selected.uuid : "";

        if (name === "title_filter") {
            // Update local state immediately so caret position is preserved (no Redux/URL on every keystroke).
            this.setState({ title_filter_input: value, offset: 0 });
            this.debouncedApplyTitleFilter(value.trim() || "", uuid);
            return;
        } else {
            // update new datasource placeholder
            this.setState(prev_state => {
                return {
                    ...prev_state,
                    [name]: value,
                };
            });
        }
    };

    componentDidMount() {
        this.loadComponent();
    }

    async componentDidUpdate(prevProps: Props, prevState: State) {
        const titleFilterChanged =
            prevProps.resource_edit_filters.title_filter !== this.props.resource_edit_filters.title_filter;
        const lineGroupChanged =
            prevProps.resource_edit_filters.line_group_uuid !== this.props.resource_edit_filters.line_group_uuid;

        // Sync search input from props when filter is changed externally (e.g. URL/back or after debounce).
        if (titleFilterChanged) {
            this.setState({ title_filter_input: this.props.resource_edit_filters.title_filter ?? "" });
        }
        if (lineGroupChanged || titleFilterChanged) {
            this.setState({ materials: [], has_next_page: true });
        }

        if (
            lineGroupChanged ||
            prevState.offset !== this.state.offset ||
            titleFilterChanged ||
            prevState.selected_type_external !== this.state.selected_type_external
        ) {
            await this.loadComponent();
        }
    }

    /**
     * Handle `click` events for material items.
     */
    handleMaterialClick = async (material: t.IMaterialModel) => {
        const plant_tags_res = await getBackend().manufacturing.getMaterialPlantTags({ uuid: material.uuid });

        this.setState({
            selected: material,
            selected_plant_tags: plant_tags_res.data,
        });

        // get child materials in case it's a parallel material
        if (this.props.resource_edit_filters.line_group_uuid === PARALLEL_MATERIALS_ID) {
            const { data } = await getBackend().manufacturing.parallelMaterialGet({ uuid: material.uuid });
            this.setState({ selected: material, parallel_selected: data });
        } else {
            this.setState({ selected: material, parallel_selected: null });
        }

        this.props.history.push(`/digital-twin/resources/materials/${material.uuid}`);
    };

    onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target;
        if (target instanceof HTMLDivElement) {
            const percentage = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
            if (percentage > 80 && this.state.has_next_page) {
                this.setState({
                    offset: this.state.offset + 1,
                    has_next_page: false,
                    is_pagination: true,
                });
            }
        }
    };

    handleTabChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        Redirect.resourceEditFilters(this.props.history, "/digital-twin/resources/materials/", {
            ...this.props.resource_edit_filters,
            line_group_uuid: event.currentTarget.value,
        });
    };

    handleTypeExternalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const target = e.target;
        if (target instanceof HTMLSelectElement) {
            const value = target.value;
            this.setState({
                selected_type_external: value !== "" ? value : null,
                is_pagination: false,
            });
        }
    };

    render() {
        const is_parallel = this.props.resource_edit_filters.line_group_uuid === PARALLEL_MATERIALS_ID;
        return (
            <ResourcesLayout>
                <ResourcesLayout.Main>
                    <ResourcesLayout.List onScroll={this.onScroll}>
                        <ResourcesLayout.Search id="materials-search">
                            <MaterialTabs is_parallel={is_parallel} onChange={this.handleTabChange} />
                            <div className="form-row flex-nowrap mx-0">
                                <span className="form-group flex-grow-1 ml-0">
                                    <input
                                        type="text"
                                        className="form-control search_bar"
                                        placeholder={translate("common.search", "Search") + " ..."}
                                        name="title_filter"
                                        value={this.state.title_filter_input}
                                        onChange={this.handleInputChange}
                                    />
                                </span>
                                <span className="form-group">
                                    {is_parallel ? (
                                        <Authorization.Link
                                            to="/digital-twin/resources/material/create"
                                            className="btn btn-inv-primary btn-icon btn-icon-add"
                                            title={translate(
                                                "Manufacturing.Materials.add_new_parallel_material",
                                                "Add new multipart material"
                                            )}
                                            permission={Auth.PERMISSION_NAMES.MultipartMaterialAdd}
                                        >
                                            <span className="sr-only">
                                                <FormattedMessage
                                                    id="Manufacturing.Materials.add_new_parallel_material"
                                                    defaultMessage="Add new multipart material"
                                                />
                                            </span>
                                        </Authorization.Link>
                                    ) : (
                                        <select
                                            name="type_externals"
                                            className="form-control select_filter_control"
                                            value={this.state.selected_type_external || ""}
                                            onChange={this.handleTypeExternalChange}
                                        >
                                            <option value="">
                                                {translate(
                                                    "tag_groups.MATERIAL_TAGS.values.title.type_external",
                                                    "Type externals"
                                                )}
                                            </option>
                                            {this.state.type_externals.map((type_external, i) => {
                                                if (type_external) {
                                                    return (
                                                        <option key={i} value={type_external}>
                                                            {type_external}
                                                        </option>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </select>
                                    )}
                                </span>
                            </div>
                        </ResourcesLayout.Search>
                        <MaterialsList
                            materials={this.state.materials}
                            parallel_materials={this.state.parallel_materials}
                            selected={this.state.selected}
                            parallel_selected={this.state.parallel_selected}
                            filter={""}
                            handleMaterialClick={this.handleMaterialClick}
                        />
                    </ResourcesLayout.List>

                    <ResourcesLayout.Content>
                        <ErrorComponent msg={this.state.error} type="error" />
                        <ErrorComponent msg={this.state.warning} type="warning" />
                        <MaterialsDetails
                            selected={this.state.selected}
                            selected_plant_tags={this.state.selected_plant_tags}
                            parallel_selected={this.state.parallel_selected}
                        />
                    </ResourcesLayout.Content>
                </ResourcesLayout.Main>
            </ResourcesLayout>
        );
    }
}

const mapState = (state: ReduxState) => {
    return { resource_edit_filters: state.resource_edit_filters };
};

type ReduxProps = ReturnType<typeof mapState>;

export default connect<ReduxProps, {}, OwnProps, ReduxState>(mapState)(Materials);
